// API Configuration
// In production, use relative URLs (proxied through Caddy)
// In development, use localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' 
    ? '' // Relative URLs in production (proxied by Caddy)
    : 'http://127.0.0.1:8000'); // Localhost for development

// Token management utilities
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Helper function to get stored tokens
const getStoredTokens = () => {
  return {
    accessToken: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY)
  };
};

// Helper function to store tokens
const storeTokens = (accessToken, refreshToken) => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

// Helper function to clear tokens
const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // FastAPI 422s return detail as a list of {loc, msg} objects
    const detail = Array.isArray(errorData.detail)
      ? errorData.detail.map((e) => `${(e.loc || []).slice(1).join('.')}: ${e.msg}`).join('; ')
      : errorData.detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url, options = {}) => {
  const { accessToken } = getStoredTokens();
  
  if (!accessToken) {
    throw new Error('No access token available');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    },
  });

  // If token expired, try to refresh
  if (response.status === 401) {
    try {
      await refreshAccessToken();
      // Retry the request with new token
      const newToken = localStorage.getItem(TOKEN_KEY);
      return fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newToken}`,
          ...options.headers,
        },
      });
    } catch (refreshError) {
      // Refresh failed, redirect to login
      clearTokens();
      window.location.reload(); // This will trigger the login screen
      throw new Error('Session expired. Please login again.');
    }
  }

  return response;
};

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    
    if (response.ok) {
      const data = await response.json();
      storeTokens(data.access_token, data.refresh_token);
      return { 
        success: true, 
        username: data.username,
        expiresIn: data.expires_in 
      };
    } else {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Invalid credentials');
    }
  },

  logout: async () => {
    try {
      const { accessToken } = getStoredTokens();
      if (accessToken) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
    }
  },

  refreshAccessToken: async () => {
    const { refreshToken } = getStoredTokens();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      storeTokens(data.access_token, data.refresh_token);
      return data;
    } else {
      clearTokens();
      throw new Error('Failed to refresh token');
    }
  },

  getCurrentUser: async () => {
    const { accessToken } = getStoredTokens();
    if (!accessToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        return await response.json();
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  isAuthenticated: () => {
    const { accessToken } = getStoredTokens();
    return !!accessToken;
  }
};

// Export the refresh function for use in other modules
export const refreshAccessToken = authAPI.refreshAccessToken;

// Associations API
export const associationsAPI = {
  getAll: async () => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/associations`);
    return handleResponse(response);
  },

  create: async (association) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/associations`, {
      method: 'POST',
      body: JSON.stringify({
        name: association.name,
        address: association.address,
        description: association.description || '',
        active: true
      }),
    });
    return handleResponse(response);
  },

  update: async (id, association) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/associations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: association.name,
        address: association.address,
        description: association.description || '',
        active: association.active
      }),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/associations/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  }
};

// Services API
export const servicesAPI = {
  getAll: async (associationId = null) => {
    let url = `${API_BASE_URL}/admin/services`;
    if (associationId) {
      url += `?association_id=${associationId}`;
    }
    
    const response = await makeAuthenticatedRequest(url);
    return handleResponse(response);
  },

  create: async (service) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services`, {
      method: 'POST',
      body: JSON.stringify(service),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${id}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  update: async (id, serviceData) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
    return handleResponse(response);
  },

  generateSlots: async (id) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${id}/generate-slots`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  getReservationsWithSlots: async (id, onlyConfirmed = true) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${id}/reservations-with-slots?only_confirmed=${onlyConfirmed}`);
    return handleResponse(response);
  }
};

// Reservations API
export const reservationsAPI = {
  getByService: async (serviceId, onlyConfirmed = true) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${serviceId}/reservations?only_confirmed=${onlyConfirmed}`);
    return handleResponse(response);
  },

  downloadPDF: async (serviceId, onlyConfirmed = true) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${serviceId}/download-pdf?only_confirmed=${onlyConfirmed}`);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservations_${serviceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      throw new Error('Failed to download PDF');
    }
  },

  sendPDFEmail: async (serviceId, onlyConfirmed = true) => {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/admin/services/${serviceId}/send-pdf-email?only_confirmed=${onlyConfirmed}`, {
      method: 'POST',
    });
    
    return handleResponse(response);
  }
}; 